import { useMemo, useState } from "react";
import { t } from "i18next";
import type { PaginationState } from "@tanstack/react-table";

import { pageCount } from "@/core/api/pagination";
import {
  DataTableClar,
  type ColumnsClarFactory,
} from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { CopyButton } from "@/core/components/ui/copy-button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { useSavingsVaultMovements } from "@/features/savings/api/use-savings-vault-movements";
import type { SavingsMovementDto } from "@/features/savings/api/dto";
import type {
  SavingsMovementDirection,
  SavingsMovementStatus,
} from "@/features/savings/api/enums";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/** Ligne de l'historique : `DataTableClar` exige un `id` requis. */
export type SavingsMovementRow = SavingsMovementDto & { id: string };

const STATUS_VARIANTS: Record<
  SavingsMovementStatus,
  "warning" | "success" | "destructive"
> = {
  PENDING: "warning",
  COMPLETED: "success",
  FAILED: "destructive",
};

const DIRECTION_VARIANTS: Record<
  SavingsMovementDirection,
  "secondary" | "outline"
> = {
  DEBIT: "secondary",
  CREDIT: "outline",
};

/**
 * Colonnes de l'historique des mouvements (`GET .../movements`) — lecture
 * paginée GET, aucun filtre documenté côté API : tout en `denied`.
 * Montants, références et IDs en `NoTranslate`.
 */
const movementsColumns: ColumnsClarFactory<SavingsMovementRow> = () => [
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("savings.vaults.movements.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "direction",
    accessorKey: "direction",
    header: t("savings.vaults.movements.columns.direction"),
    cell: ({ getValue }) => {
      const direction = getValue() as SavingsMovementDirection | undefined;
      return direction ? (
        <Badge variant={DIRECTION_VARIANTS[direction] ?? "outline"}>
          {t(`savings.enums.movementDirection.${direction}`)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "amountMinor",
    accessorKey: "amountMinor",
    header: t("savings.vaults.movements.columns.amount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.amountMinor}
        currency={row.original.currency}
        className="font-medium"
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "walletTransactionReference",
    accessorKey: "walletTransactionReference",
    header: t("savings.vaults.movements.columns.reference"),
    cell: ({ getValue }) => {
      const reference = (getValue() as string | null | undefined)?.trim();
      if (!reference) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <NoTranslate as="span" className="font-mono text-xs">
            {reference}
          </NoTranslate>
          <CopyButton value={reference} />
        </span>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("savings.vaults.movements.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as SavingsMovementStatus | undefined;
      return status ? (
        <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
          {t(`savings.enums.movementStatus.${status}`)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "failureReason",
    accessorKey: "failureReason",
    header: t("savings.vaults.movements.columns.failureReason"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="line-clamp-1 text-sm">
        {(getValue() as string | null) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
];

/**
 * Onglet « Mouvements » du détail coffre : versements / retraits paginés
 * (du plus récent au plus ancien), référence wallet `FPT-…` et motif
 * d'échec le cas échéant.
 */
export function VaultMovementsSection({ vaultId }: { vaultId: string }) {
  const { t } = useTypedTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSavingsVaultMovements(vaultId, params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((m): m is SavingsMovementRow => Boolean(m.id)),
    [data]
  );

  return (
    <DataTableClar
      data={rows}
      columnsClar={movementsColumns}
      title={t("savings.vaults.movements.title")}
      exportFileName="savings-vault-movements"
      paginationMode="server"
      serverPageCount={data ? pageCount(data) : 0}
      totalItemsCount={data?.total ?? 0}
      pagination={pagination}
      onPaginationChange={setPagination}
      loading={{ isLoading, isFetching }}
      error={
        error
          ? { message: error.message, refetch: () => void refetch() }
          : undefined
      }
    />
  );
}
